import React, { useMemo } from 'react';
import { node } from 'prop-types';
import { useSelector } from 'react-redux';
import featureToggle, { MARGIN_TRADING } from '@config/featureToggle';
import { Loader } from 'Component/Loader';
import { useUserData } from '@services/users/me';

import {
  COIN_STATUS,
  STATUS_ACCOUNT_ACTIVE,
  STATUS_ACCOUNT_KYC_FAILED,
  STATUS_ACCOUNT_SUSPENDED,
  STATUS_KYC_NOT_READY,
  STATUS_KYC_REJECT,
  STATUS_KYC_FAILED,
  STATUS_KYC_VERIFIED,
  STATUS_KYC_IN_PROCESS,
  STATUS_KYC_READY_FOR_DOWNLOAD,
  STATUS_KYC_READY_TO_PROCESS,
  STATUS_KYC_DOWNLOADED,
  STATUS_MT_ACTIVE,
  STATUS_MT_IN_PROCESS,
  STATUS_MT_REJECTED,
  STATUS_MT_INACTIVE,
  STATUS_PREMIUM_INACTIVE,
  STATUS_KYC_ERROR,
  STATUS_KYC_READY_TO_REVIEW,
} from './constants';

export const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const { userData, isLoading } = useUserData();
  const authDataLoading = isLoading;
  const statusMT = userData?.status_mt_account;
  const featMarginTrading = featureToggle.some(
    (feat) => feat.feature === MARGIN_TRADING && feat.toggle
  );
  const coinInfo = userData?.coin_info;
  const homeData = useSelector((state) => state.auth.homeData) || {};
  const kycStatusData = useSelector((state) => state.auth.kycStatusData) || {};
  const stockKycError = kycStatusData?.stock_status !== 'OK';
  const statusAccount = kycStatusData?.stock?.status_account;
  const statusPremium = kycStatusData?.stock?.status_is_premium;
  const statusKyc = kycStatusData?.mf?.status_kyc;
  const kycLevel = kycStatusData?.kyc_level;
  const emailVerified = homeData.is_email_verified;

  const isCoinInactive = useMemo(() => coinInfo?.coin_status === COIN_STATUS.inActive, [coinInfo]);

  const isCoinInprogress = useMemo(
    () => coinInfo?.coin_status === COIN_STATUS.inProgress,
    [coinInfo]
  );

  const isCoinActive = useMemo(() => coinInfo?.coin_status === COIN_STATUS.active, [coinInfo]);

  const isCoinReject = useMemo(() => coinInfo?.coin_status === COIN_STATUS.reject, [coinInfo]);

  const stockKycVerified = useMemo(() => statusAccount === STATUS_ACCOUNT_ACTIVE, [statusAccount]);
  const stockKycRejected = useMemo(
    () => statusAccount === STATUS_ACCOUNT_KYC_FAILED || statusAccount === STATUS_ACCOUNT_SUSPENDED,
    [statusAccount]
  );
  const mfKycRejected = useMemo(() => statusKyc === STATUS_KYC_REJECT, [statusKyc]);
  const mfKycFailed = useMemo(() => statusKyc === STATUS_KYC_FAILED, [statusKyc]);
  const mfKycError = useMemo(() => statusKyc === STATUS_KYC_ERROR, [statusKyc]);
  const mfKycVerified = useMemo(() => statusKyc === STATUS_KYC_VERIFIED, [statusKyc]);
  const mfKycVerifying = useMemo(
    () =>
      statusKyc === STATUS_KYC_IN_PROCESS ||
      statusKyc === STATUS_KYC_READY_FOR_DOWNLOAD ||
      statusKyc === STATUS_KYC_READY_TO_PROCESS ||
      statusKyc === STATUS_KYC_READY_TO_REVIEW ||
      statusKyc === STATUS_KYC_DOWNLOADED,
    [statusKyc]
  );
  const userNotKycYet = useMemo(() => statusKyc === STATUS_KYC_NOT_READY, [statusKyc]);
  const userNotKycStockYet = useMemo(
    () => kycLevel !== 2 && mfKycVerified && !stockKycError,
    [statusKyc, kycLevel]
  );
  const userNotPremium = useMemo(() => statusPremium === STATUS_PREMIUM_INACTIVE);
  const stockKycVerifying = useMemo(
    () => kycLevel === 2 && !stockKycVerified && !stockKycRejected && !stockKycError
  );
  const mtActive = useMemo(() => statusMT === STATUS_MT_ACTIVE && featMarginTrading, [statusMT]);
  const mtInactive = useMemo(
    () => (statusMT === STATUS_MT_INACTIVE || !statusMT) && featMarginTrading,
    [statusMT]
  );
  const mtProcess = useMemo(
    () => statusMT === STATUS_MT_IN_PROCESS && featMarginTrading,
    [statusMT]
  );
  const mtRejected = useMemo(
    () => statusMT === STATUS_MT_REJECTED && featMarginTrading,
    [statusMT]
  );

  if (authDataLoading) {
    return (
      <Loader
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      />
    );
  }

  const valueProps = {
    coinInfo,
    coinStatus: {
      isCoinActive,
      isCoinReject,
      isCoinInactive,
      isCoinInprogress,
    },
    loadingAuth: authDataLoading,
    emailVerified,
    featMarginTrading,
    userNotKycStockYet,
    userNotKycYet,
    userNotPremium,
    stockKycError,
    stockKycVerified,
    stockKycRejected,
    stockKycVerifying,
    mfKycFailed,
    mfKycRejected,
    mfKycVerified,
    mfKycVerifying,
    mtActive,
    mtInactive,
    mtProcess,
    mtRejected,
    mfKycError,
    userId: userData?.id,
  };

  return <AuthContext.Provider value={valueProps}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: node.isRequired,
};

export default AuthProvider;
